import React, { useEffect } from "react";
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { Button } from "../assets/styles/ButtonStyle";
import { H3, Body2 } from "../assets/styles/Typography";
import MoleculeHeader from "./Global/components/molecules/MoleculeHeader";
import {
  ContentsWrap,
  MainContent,
} from "../assets/styles/BusinessAnalysisStyle";

import { getUserMemberType } from "../utils/indexedDB";
import DeviceDetectionService from "../utils/DeviceDetectionService";

const PageDeviceSelection = () => {
  const navigate = useNavigate();
  const [selectedDeviceType, setSelectedDeviceType] = React.useState(null);
  const [memberType, setMemberType] = React.useState(null);

  useEffect(() => {
    const checkUserAccess = async () => {
      const accessToken = sessionStorage.getItem("accessToken");

      if (!accessToken) {
        console.log("❌ 토큰 없음 - 로그인 페이지로 이동");
        navigate("/Login");
        return;
      }

      try {
        const userMemberType = await getUserMemberType(accessToken);
        console.log(
          "🔍 디바이스 선택 페이지 - 사용자 멤버 타입:",
          userMemberType?.member_type
        );

        // 3으로 시작하는 멤버 타입만 허용
        const memberTypeStr = String(userMemberType?.member_type);
        if (!memberTypeStr.startsWith("3")) {
          console.log("❌ 권한 없음 - 홈으로 이동");
          navigate("/");
          return;
        }

        setMemberType(userMemberType?.member_type);

        // 원래 목적지가 세션에 저장되어 있는지 확인
        const originalDestination = sessionStorage.getItem(
          "deviceSelectionDestination"
        );
        console.log("🎯 저장된 목적지:", originalDestination);
      } catch (error) {
        console.error("❌ 사용자 정보 확인 실패:", error);
        navigate("/Login");
      }
    };

    checkUserAccess();
  }, [navigate]);

  // 디바이스 선택 핸들러
  const handleDeviceSelection = (deviceType) => {
    console.log(
      `🎯 사용자가 선택한 기기 타입: ${deviceType} (멤버 타입: ${memberType})`
    );

    let finalDeviceType = deviceType;

    // 자동 감지인 경우 실제 기기를 감지
    if (deviceType === "auto") {
      try {
        const detectedType = DeviceDetectionService.detectDevice();
        finalDeviceType = detectedType;
        console.log(`🔍 자동 감지 결과: ${detectedType}`);

        // 감지 결과 알림
        const deviceName =
          detectedType === "desktop"
            ? "데스크탑"
            : detectedType === "android"
            ? "안드로이드"
            : detectedType === "ios"
            ? "iOS"
            : "모바일";
        alert(`🔍 자동 감지 완료!\n감지된 기기: ${deviceName}`);
      } catch (error) {
        console.error("❌ 자동 감지 실패:", error);
        alert(
          "기기 자동 감지 중 오류가 발생했습니다. 데스크탑으로 설정합니다."
        );
        finalDeviceType = "desktop";
      }
    }

    // DeviceDetectionService를 사용해서 기기 타입 설정
    try {
      const updatedType =
        DeviceDetectionService.updateDeviceType(finalDeviceType);
      console.log(`✅ 기기 타입 업데이트 완료: ${updatedType}`);
    } catch (error) {
      console.error("❌ 기기 타입 설정 실패:", error);
      alert("기기 모드 설정 중 오류가 발생했습니다.");
      return;
    }

    const displayName =
      finalDeviceType === "desktop"
        ? "데스크탑"
        : finalDeviceType === "android"
        ? "안드로이드"
        : finalDeviceType === "ios"
        ? "iOS"
        : "모바일";

    console.log(`📱 기기 모드 설정 완료: ${displayName}`);

    // 원래 목적지로 이동
    const originalDestination = sessionStorage.getItem(
      "deviceSelectionDestination"
    );
    sessionStorage.removeItem("deviceSelectionDestination"); // 사용 후 제거

    if (originalDestination) {
      console.log(`🎯 목적지로 이동: ${originalDestination}`);
      navigate(originalDestination);
    } else {
      // 기본값: 멤버 타입에 따른 기본 목적지
      console.log(`🎯 기본 목적지로 이동 (멤버 타입: ${memberType})`);
      if (memberType === 31) {
        navigate("/EducationDashBoard");
      } else if (memberType === 32) {
        navigate("/EduboardManage");
      } else {
        navigate("/CustomerManage"); // 기본값
      }
    }
  };

  return (
    <>
      <ContentsWrap>
        <MoleculeHeader />
        <MainContent>
          <CenteredWrapper>
            <TitleArea>
              <H3 style={{ textAlign: "center", marginBottom: 20 }}>
                📱 디바이스 타입 선택
                <br />
                최적화된 사용 환경 설정
              </H3>
              <Body2
                color="gray600"
                style={{ textAlign: "center", marginBottom: 40 }}
              >
                사용하시는 디바이스에 맞는 최적화된 인터페이스를 제공합니다.
                <br />
                디바이스 타입을 선택해주세요.
              </Body2>
            </TitleArea>

            {/* 디바이스 선택 UI */}
            <DeviceSelectionRow>
              <DeviceCard>
                <DeviceIcon>🖥️</DeviceIcon>
                <DeviceTitle>OPS 모드</DeviceTitle>
                <DeviceDesc>
                  OPS 환경에 최적화된 인터페이스입니다.
                  {/* <br />
                  • 전체화면 모드 지원
                  <br />
                  • 키보드 단축키 지원
                  <br />• 고해상도 디스플레이 최적화 */}
                </DeviceDesc>
                <Button
                  Primary
                  Large
                  Fill
                  style={{ width: "100%", marginTop: 18 }}
                  onClick={() => handleDeviceSelection("desktop")}
                >
                  OPS로 계속하기
                </Button>
              </DeviceCard>

              <DeviceCard>
                <DeviceIcon>📱</DeviceIcon>
                <DeviceTitle>Android 모드</DeviceTitle>
                <DeviceDesc>
                  Android 환경에 최적화된 인터페이스입니다.
                  {/* <br />• 터치 인터페이스 최적화
                  <br />
                  • 스크롤 기반 네비게이션
                  <br />• Android 스케일링 문제 해결 */}
                </DeviceDesc>
                <Button
                  Primary
                  Large
                  Fill
                  style={{
                    width: "100%",
                    marginTop: 18,
                    backgroundColor: "#FF9800",
                  }}
                  onClick={() => handleDeviceSelection("android")}
                >
                  Android로 계속하기
                </Button>
              </DeviceCard>

              {/* <DeviceCard>
                <DeviceIcon>🔍</DeviceIcon>
                <DeviceTitle>자동 감지 모드</DeviceTitle>
                <DeviceDesc>
                  현재 브라우저와 기기를 자동으로 감지합니다.
                  <br />
                  • 실제 사용 환경 기반 감지
                  <br />
                  • 화면 크기 및 User Agent 분석
                  <br />• 최적화된 인터페이스 자동 선택
                </DeviceDesc>
                <Button
                  Primary
                  Large
                  Fill
                  style={{
                    width: "100%",
                    marginTop: 18,
                    backgroundColor: "#4CAF50",
                  }}
                  onClick={() => handleDeviceSelection("auto")}
                >
                  자동 감지로 계속하기
                </Button>
              </DeviceCard> */}
            </DeviceSelectionRow>
          </CenteredWrapper>
        </MainContent>
      </ContentsWrap>
    </>
  );
};

export default PageDeviceSelection;

// 스타일 컴포넌트들
const TitleArea = styled.div`
  text-align: center;
  margin-bottom: 32px;
`;

const CenteredWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: calc(100vh - 100px);
  width: 100%;
  padding: 40px 20px;
`;

const DeviceSelectionRow = styled.div`
  display: flex;
  gap: 24px;
  justify-content: center;
  margin-top: 20px;
  flex-wrap: wrap;
  max-width: 1200px;
  margin-left: auto;
  margin-right: auto;
`;

const DeviceCard = styled.div`
  width: 320px;
  min-height: 320px;
  background: #fff;
  border-radius: 20px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  padding: 32px 24px 24px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  border: 2px solid transparent;
  transition: all 0.3s ease;
  flex: 1;
  max-width: 320px;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
    border-color: #256ef4;
  }

  @media (max-width: 1024px) {
    width: 280px;
    max-width: 280px;
  }
`;

const DeviceIcon = styled.div`
  font-size: 60px;
  margin-bottom: 24px;
  filter: drop-shadow(0 4px 8px rgba(0, 0, 0, 0.1));
`;

const DeviceTitle = styled.div`
  font-size: 22px;
  font-weight: 700;
  color: #256ef4;
  margin-bottom: 16px;
  text-align: center;
`;

const DeviceDesc = styled(Body2)`
  color: #464c53;
  font-size: 16px;
  text-align: center;
  margin-bottom: 12px;
  flex-grow: 1;
  line-height: 1.6;
`;
